/**
 * MB Quick View Drawer
 * Handles product quick view modal functionality with caching and prefetch
 * Uses quick-view attribute with product handle
 * Extracts <main> content from product.quickview template
 */

const QuickViewManager = (() => {
  const QV_SELECTOR = 'main';

  const cache = new Map();      // handle -> { html, ts }
  const inflight = new Map();   // handle -> Promise<string>
  const CACHE_TTL_MS = 5 * 60 * 1000;

  /**
   * @param {string} handle
   * @returns {string | null}
   */
  function cacheGet(handle) {
    const entry = cache.get(handle);
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
      cache.delete(handle);
      return null;
    }
    return entry.html;
  }

  /**
   * @param {string} handle
   * @param {string} html
   */
  function cacheSet(handle, html) {
    cache.set(handle, { html, ts: Date.now() });
  }

  /**
   * @param {string} handle
   * @param {string} view - The template view to use (default: 'quickview')
   * @returns {string}
   */
  function buildProductURL(handle, view = 'quickview') {
    const url = new URL(`/products/${handle}`, window.location.origin);

    // Use template view for visual composability
    url.searchParams.set('view', view);

    // Preserve preview/session params (theme editor / staging)
    const current = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', 'pb'].forEach((k) => {
      const value = current.get(k);
      if (value) url.searchParams.set(k, value);
    });

    return url.toString();
  }

  /**
   * @param {string} handle
   * @param {string} view - Optional template view (defaults to 'quickview')
   * @returns {Promise<string>}
   */
  async function fetchQV(handle, view = 'quickview') {
    const cacheKey = `${handle}:${view}`;
    const cached = cacheGet(cacheKey);
    if (cached) return cached;

    // If there's already a request for this handle+view, reuse it.
    if (inflight.has(cacheKey)) return inflight.get(cacheKey);

    const p = (async () => {
      const res = await fetch(buildProductURL(handle, view), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector(QV_SELECTOR);

      const out = root
        ? root.innerHTML
        : `<div style="padding:16px">Quick view content not found (missing &lt;main&gt; tag).</div>`;

      cacheSet(cacheKey, out);
      return out;
    })()
      .finally(() => {
        inflight.delete(cacheKey);
      });

    inflight.set(cacheKey, p);
    return p;
  }

  /**
   * @returns {{ dialog: HTMLElement, mount: HTMLElement } | null}
   */
  function openModal() {
    const qa = document.getElementById('quick-add-dialog');
    const mount = qa?.querySelector('#quick-add-modal-content');
    if (!qa || !mount) return null;

    // Use native DialogComponent showDialog method
    if (typeof qa.showDialog === 'function') {
      qa.showDialog();
    }
    return { dialog: /** @type {HTMLElement} */ (qa), mount: /** @type {HTMLElement} */ (mount) };
  }

  /**
   * Creates a new dynamic dialog-component for template views
   * @param {string} viewName - The view/template name to use as the dialog class
   * @returns {{ dialog: HTMLElement, mount: HTMLElement } | null}
   */
  function createDynamicModal(viewName) {
    // Ensure dialog.js is loaded
    if (!customElements.get('dialog-component')) {
      console.warn('[QV] dialog-component custom element not found. Ensure dialog.js is loaded.');
      return null;
    }

    // Create a new dialog-component wrapper
    const dialog = document.createElement('dialog-component');
    dialog.className = `sidebar ${viewName}`;
    dialog.setAttribute('data-dynamic-modal', 'true');  // Mark as dynamically created
    dialog.setAttribute('data-analytics-scope', 'quick_view');  // interaction_scope for clicks inside the quick-view modal
    
    // Create the internal dialog element
    const dialogEl = document.createElement('dialog');
    dialogEl.className = 'dialog-drawer';
    dialogEl.setAttribute('ref', 'dialog');
    dialogEl.setAttribute('scroll-lock', '');
    
    // Create close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'button button-unstyled close-button';
    closeBtn.setAttribute('aria-label', 'Close dialog');
    closeBtn.setAttribute('type', 'button');
    // Analytics attributes for the GTM click listener (quick-view modal close).
    closeBtn.setAttribute('data-analytics', 'element_click');
    closeBtn.setAttribute('data-analytics-name', 'quick_view_close');
    closeBtn.setAttribute('data-component', 'modal');
    closeBtn.setAttribute('data-cta', 'close_modal');
    closeBtn.style.zIndex = '1000';
    closeBtn.innerHTML = '<span class="svg-wrapper" aria-hidden="true"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 24 24"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/></svg></span>';
    
    // Add close button click handler
    closeBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof dialog.closeDialog === 'function') {
        dialog.closeDialog();
        document.querySelectorAll('.dialog-drawer video')?.forEach(vid => vid.pause());
      }
    });
    
    // Create content mount point
    const mount = document.createElement('div');
    mount.id = 'quick-add-modal-content-dynamic';
    mount.className = 'quick-add-modal-content';
    
    // Assemble the dialog
    dialogEl.appendChild(closeBtn);
    dialogEl.appendChild(mount);
    dialog.appendChild(dialogEl);
    
    // Add to DOM
    document.body.appendChild(dialog);
    
    // Give the custom element time to initialize
    requestAnimationFrame(() => {
      // Show the dialog
      if (typeof dialog.showDialog === 'function') {
        dialog.showDialog();
      }
    });
    
    return { dialog, mount };
  }

  /**
   * @param {string} html
   * @param {string} viewName - Optional view name for dynamic dialogs
   */
  function setModalContent(html, viewName = null) {
    let ctx;
    
    if (viewName) {
      // Create a new dynamic dialog for template views
      ctx = createDynamicModal(viewName);
    } else {
      // Use existing dialog if available
      ctx = openModal();
    }
    
    if (!ctx) return;
    ctx.mount.innerHTML = html;

    // 🔥 Dispatch event
    ctx.mount.dispatchEvent(
      new CustomEvent('mb:quickview:loaded', {
        bubbles: true,
        detail: {
          container: ctx.mount,
          view: view,
          handle: handle
        }
      })
    );

  

    if (typeof initPurchaseOptions === 'function') {
      initPurchaseOptions();
    }

    // Optional re-init hooks:
    // window.MB?.initWithin?.(ctx.mount);
  }

  return {
    fetchQV,
    cacheGet,
    setModalContent,
    createDynamicModal,
    inflight
  };
})();

// ===========================================
// Event Handlers (Attribute-based)
// ===========================================

/** @type {ReturnType<typeof setTimeout> | null} */
let prefetchTimer = null;

// Click handler - opens quick view or local dialog
document.addEventListener('click', async (e) => {
  const target = /** @type {Element | null} */ (e.target);
  const trigger = target?.closest('[quick-view]');
  if (!trigger) return;

  const handle = trigger.getAttribute('quick-view');
  const view = trigger.getAttribute('data-view') || 'quickview';
  const sidebarTitle = trigger.getAttribute('data-sidebar-title') || '';

  // Prevent navigation if this is inside an <a>
  e.preventDefault();
  e.stopPropagation();

  // First, check if there's local content on the page (static, pre-defined dialogs)
  const localDialog = document.querySelector(`.${view}:not([data-dynamic-modal])`);
  
  if (localDialog) {
    // Found static local content - use it directly
    const content = localDialog.querySelector('[data-sidebar-content]') || localDialog.querySelector('dialog');
    if (content) {
      // Use the existing local dialog component directly
      if (typeof localDialog.showDialog === 'function') {
        if (sidebarTitle) {
          const titleElement = localDialog.querySelector('[data-sidebar-title]');
          if (titleElement) {
            titleElement.textContent = sidebarTitle;
            titleElement.style.display = '';
          }
        } else {
          const titleElement = localDialog.querySelector('[data-sidebar-title]');
          if (titleElement) {
            titleElement.style.display = 'none';
          }
        }
        localDialog.showDialog();
        return;
      }
    }
  }

  // If no local content, check if we have a product handle for fetching from template
  if (!handle) return;

  // Check if there's already a dynamic dialog for this view - reuse it
  let existingDialog = document.querySelector(`.${view}[data-dynamic-modal]`);
  let ctx;
  
  if (existingDialog) {
    // Reuse existing dynamic dialog
    const mount = existingDialog.querySelector('#quick-add-modal-content-dynamic');
    if (mount) {
      ctx = { dialog: existingDialog, mount: mount };
      // Make sure it's visible
      if (typeof existingDialog.showDialog === 'function') {
        existingDialog.showDialog();
      }
    } else {
      // Remove broken dialog and create new one
      existingDialog.remove();
      ctx = QuickViewManager.createDynamicModal(view);
    }
  } else {
    // Create a new dynamic dialog for this popup
    ctx = QuickViewManager.createDynamicModal(view);
  }
  
  if (!ctx) return;

  // Load content from template
  ctx.mount.innerHTML = `<div style="padding:16px">Loading…</div>`;

  try {
    const qv = await QuickViewManager.fetchQV(handle, view);
    
    // If sidebar title is provided, prepend it to the content
    let content = qv;
    if (sidebarTitle) {
      content = `<h3 class="sidebar_title">${sidebarTitle}</h3><hr>${qv}`;
    }
    
    ctx.mount.innerHTML = content;

    // 🔥 Dispatch event
    ctx.mount.dispatchEvent(
      new CustomEvent('mb:quickview:loaded', {
        bubbles: true,
        detail: {
          container: ctx.mount,
          view: view,
          handle: handle
        }
      })
    );
    
    if (typeof initPurchaseOptions === 'function') {
      initPurchaseOptions();
    }
  } catch (err) {
    console.error('[QV] error loading template:', err);
    ctx.mount.innerHTML = `<div style="padding:16px">Quick view failed to load.</div>`;
  }
});

// Hover handler - prefetches content
document.addEventListener('mouseover', (e) => {
  const target = /** @type {Element | null} */ (e.target);
  const trigger = target?.closest('[quick-view]');
  if (!trigger) return;

  const handle = trigger.getAttribute('quick-view');
  const view = trigger.getAttribute('data-view') || 'quickview';
  const cacheKey = `${handle}:${view}`;
  
  if (!handle || QuickViewManager.cacheGet(cacheKey) || QuickViewManager.inflight.has(cacheKey)) return;

  if (prefetchTimer !== null) clearTimeout(prefetchTimer);
  prefetchTimer = setTimeout(() => {
    QuickViewManager.fetchQV(handle, view).catch(() => {});
  }, 150);
});

// Close button handler for quick view modal
document.addEventListener('click', (e) => {
  const target = /** @type {Element | null} */ (e.target);
  if (!target) return;
  
  const btn = target.closest('button');
  const isClose = btn?.getAttribute('ref') === 'closeButton';
  if (!isClose) return;

  const dialog = target.closest('quick-add-dialog')?.querySelector('dialog');
  if (!dialog) return;

  e.preventDefault();
  /** @type {HTMLDialogElement} */ (dialog).close();
});